import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import ContentLayout from "../layouts/ContentLayout";
import { isZh } from "../utils/language";
import {
  isLogged as authIsLogged,
  getUser,
  clearAuth,
} from "../utils/auth";
import PERMISSIONS from "../utils/wkCommon";
import { apiFetch } from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import PreRegToolbar from "../components/prereg/PreRegToolbar";
import PreRegTable from "../components/prereg/PreRegTable";
import AgendaCrossNavMenu from "../components/AgendaCrossNavMenu";
import "../styles/PreRegList.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PreRegList() {
  const history = useHistory();
  const { id } = useParams();

  const authUser = getUser();
  const isLogged = authIsLogged();

  const [permsReady, setPermsReady] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);

  const [loading, setLoading] = useState(false);

  const [agendaList, setAgendaList] = useState([]);
  const [selectedAgenda, setSelectedAgenda] = useState(null);

  const [list, setList] = useState([]);

  const [search, setSearch] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    personType: [],
    roleType: [],
  });

  const TXT = {
    title: isZh() ? "預先報名列表" : "Daftar Pra-Registrasi",
    subtitle: isZh()
      ? "查看已報名名單"
      : "PIC dapat melihat daftar nama yang sudah mendaftar",
  };

  // ===== PERMISSIONS =====

  useEffect(() => {
    let mounted = true;

    async function loadPerms() {
      try {
        const email = authUser?.email;

        const read = await hasUmatPermissionAsync(
          email,
          PERMISSIONS.AGENDA_PREREG_READ
        );

        const write = await hasUmatPermissionAsync(
          email,
          PERMISSIONS.AGENDA_PREREG_WRITE
        );

        if (mounted) {
          setCanRead(Boolean(read));
          setCanWrite(Boolean(write));
        }
      } catch (e) {
        console.warn(e);

        if (
          e.message === "AUTH_INVALID" ||
          e.message === "HTTP_401" ||
          e.message === "HTTP_403"
        ) {
          clearAuth();
          window.location.href = "/";
        }
      } finally {
        if (mounted) setPermsReady(true);
      }
    }

    if (isLogged) loadPerms();
    else setPermsReady(true);

    return () => {
      mounted = false;
    };
  }, [isLogged, authUser]);

  // ===== LOAD AGENDAS =====

// ===== LOAD AGENDAS =====

useEffect(() => {

  if (!canRead) return;

  async function loadAgenda() {
    try {
      const url =
        `/api/agenda-pre-reg-forms?` +
        `populate%5Bagenda%5D%5Bfields%5D%5B0%5D=subject` +
        `&populate%5Bagenda%5D%5Bfields%5D%5B1%5D=subject_zh` +
        `&populate%5Bagenda%5D%5Bfields%5D%5B2%5D=agendaDate` +
        `&sort%5B0%5D=createdAt:desc`;
      const res =
        await apiFetch(url);
      const forms =
        res?.data || [];
      const mapped =
        forms
          .filter((f) => f?.agenda)
          .map((f) => ({
            documentId:
              f.agenda.documentId,
            subject:
              f.agenda.subject,
            subject_zh:
              f.agenda.subject_zh,
            agendaDate:
              f.agenda.agendaDate,
            preRegFormCode:
              f.preRegFormCode,
          }));
      setAgendaList(mapped);
    } catch (e) {
      console.error(e);
    }
  }
  loadAgenda();
}, [canRead]);

  // ===== LOAD DATA =====

  useEffect(() => {
    if (!canRead || !selectedAgenda) {
      setList([]);
      return;
    }

    async function loadData() {
      setLoading(true);

      try {
        let url =
          `/api/agenda-pre-registrations?` +
          `populate[0]=agenda` +
          `&populate[1]=umat` +
          `&populate[2]=external_person` +
          `&sort[0]=createdAt:desc`;

        if (selectedAgenda) {
          url +=
            `&filters%5Bagenda%5D%5BdocumentId%5D%5B$eq%5D=` +
            encodeURIComponent(selectedAgenda);
        }

        const res = await apiFetch(url);

        setList(res?.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [canRead, selectedAgenda]);

  // ===== HELPERS =====

  function getStatus(item) {
    if (item?.event_participant) return "confirmed";
    if (item?.peserta === false) return "draft";
    return "waiting";
  }

  useEffect(() => {
    if (id && agendaList.length > 0) {
      const found =
        agendaList.find(
          (x) =>
            x.documentId === id
        );

      if (
        found &&
        selectedAgenda !== found.documentId
      ) {

        setSelectedAgenda(
          found.documentId
        );
      }
    }
  }, [id, agendaList, selectedAgenda]);

  const selectedAgendaObj =
    agendaList.find(
      (x) =>
        x.documentId === selectedAgenda
    );
      
  // ===== FILTERED =====

  const filtered = useMemo(() => {
    return list.filter((item) => {
      const name =
        (
          item?.umat?.fullName ||
          item?.external_person?.name ||
          item?.nonUmatName ||
          ""
        ).toLowerCase();

      const q = search.toLowerCase();

      if (q && !name.includes(q)) return false;

      const status = getStatus(item);

      if (
        filters.status.length > 0 &&
        !filters.status.includes(status)
      ) {
        return false;
      }

      if (
        filters.personType.length > 0 &&
        !filters.personType.includes(item.personType)
      ) {
        return false;
      }

      const role = item?.peserta ? "peserta" : "petugas";

      if (
        filters.roleType.length > 0 &&
        !filters.roleType.includes(role)
      ) {
        return false;
      }

      return true;
    });
  }, [list, search, filters]);

  if (!permsReady) return null;

  if (!canRead) {
    return (
      <div style={{ padding: "1rem" }}>
        {isZh() ? "沒有權限" : "Tidak memiliki akses"}
      </div>
    );
  }

  return (
    <div className="prereg-page">
      <ContentLayout
        title={TXT.title}
        headerActions={
          <>
            {canWrite && (
              <button
                className="btn-icon add-btn"
                title="Add"
                onClick={() => {
                  if (!selectedAgendaObj) {
                    alert(
                      isZh()
                        ? "請先選擇活動"
                        : "Harap pilih agenda terlebih dahulu"
                    );
                    return;
                  }

                  history.push(
                    `/f?${encodeURIComponent(
                      selectedAgendaObj.preRegFormCode
                    )}&pic`
                  );
                }}
              >
                ＋
              </button>
            )}
            {selectedAgenda && (
              <div style={{ position: "relative" }}>
                <button
                  className="btn-icon more-btn"
                  onClick={() =>
                    setShowMenu((s) => !s)
                  }
                >
                  ⋯
                </button>
                {showMenu && (
                  <div className="action-menu">
                    <AgendaCrossNavMenu
                      agendaId={selectedAgenda}
                      hasPreRegForm={!!selectedAgendaObj?.preRegFormCode}                      
                      onClose={() =>
                        setShowMenu(false)
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </>
        }
        hideSearch
        singleColumn={true}
        infoNode={
          <div className="tip-text">
            {TXT.subtitle}
          </div>
        }
        mainNode={
            <>
              <div className="prereg-main-wrap">

                <PreRegToolbar
                  agendaList={agendaList}
                  selectedAgenda={selectedAgenda}
                  setSelectedAgenda={setSelectedAgenda}
                  search={search}
                  setSearch={setSearch}
                  filters={filters}
                  setFilters={setFilters}
                  filtered={filtered}
                  canWrite={canWrite}
                />

                {!selectedAgenda ? (
                  <div className="prereg-empty-select">
                    {isZh()
                      ? "請先選擇活動"
                      : "Silakan pilih agenda terlebih dahulu"}
                  </div>
                ) : (
                  <PreRegTable
                    data={filtered}
                    loading={loading}
                    canWrite={canWrite}
                  />
                )}
              </div>

              <style>{`
              .prereg-empty-select{
                background:#fff;
                border:1px dashed #cbd5e1;
                border-radius:1rem;
                padding:3rem 1.5rem;
                text-align:center;
                font-size:0.95rem;
                color:#64748b;
              }
              `}</style>
            </>
        }
      />
    </div>
  );
}